import { Info } from "lucide-react";
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Button } from "@/components/ui/button";

export function HelpTooltip({ label, children, trigger, className, text }: {
  label: string;
  children: ReactNode;
  trigger?: ReactNode;
  className?: string;
  text?: string;
}): JSX.Element {
  const [position, setPosition] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const id: string = useId();
  useEffect(() => {
    if (!position) return;
    const closeOutside = (event: PointerEvent): void => {
      if (event.target instanceof Node && !triggerRef.current?.contains(event.target)
        && !tooltipRef.current?.contains(event.target)) setPosition(null);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [position]);
  const showTooltip = (): void => {
    const bounds: DOMRect | undefined = triggerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    const width: number = Math.min(320, window.innerWidth - 32);
    setPosition({
      width,
      left: Math.max(16, Math.min(bounds.left, window.innerWidth - width - 16)),
      ...(bounds.bottom > window.innerHeight / 2
        ? { bottom: window.innerHeight - bounds.top + 8 }
        : { top: bounds.bottom + 8 }),
    });
  };
  return (
    <span className="relative inline-flex shrink-0">
      <Button ref={triggerRef} variant="ghost" size="sm" className={className ?? "h-7 w-7 p-0"}
        aria-label={label} aria-describedby={position ? id : undefined}
        onPointerMove={(event) => { if (event.pointerType !== "touch") showTooltip(); }}
        onPointerLeave={(event) => { if (event.pointerType !== "touch") setPosition(null); }}
        onFocus={showTooltip} onBlur={() => setPosition(null)}
        onClick={showTooltip}
        onKeyDown={(event) => { if (event.key === "Escape") setPosition(null); }}>
        {trigger ?? <Info className="h-4 w-4" aria-hidden="true" />}
        {text}
      </Button>
      {position ? createPortal(
        <span ref={tooltipRef} id={id} role="tooltip" style={position}
          className="fixed z-50 max-h-[calc(100vh-2rem)] overflow-y-auto break-words rounded-md border border-border bg-popover p-4 text-sm font-normal text-popover-foreground shadow-md">
          {children}
        </span>, document.body,
      ) : null}
    </span>
  );
}
